import React from "react";
import { CheckIcon, RefreshCcw } from "lucide-react";
import { useUtils } from "@/app/hooks/useUtils";
import onHold from "@/assets/images/onhold.svg";

export interface CmAppointmentConfirmationData {
  booking_id: string;
  parent_name: string;
  pet_name: string;
  pet_species_name: string;
  pet_breed_name: string;
  date: string;
  start_time: string;
  end_time: string;
  service_type_name: string;
  sub_service_name: string;
  staff_name: string;
  clinic_name: string;
  payment_collected: boolean;
  payment_pending?: boolean;
  booking_fee?: number;
}

interface CmAppointmentConfirmationProps {
  rows: CmAppointmentConfirmationData[];
  mobile: string;
  activeBooking?: CmAppointmentConfirmationData | null;
  onRefreshPayment?: (bookingId: string) => void;
  onResendWhatsApp?: (bookingId: string, petName: string) => void;
  isRefreshingPayment?: boolean;
}

const joinBreed = (species: string, breed: string) => {
  if (species && breed) return `${species} - ${breed}`;
  return species || breed || "-";
};

export const CmAppointmentConfirmation: React.FC<CmAppointmentConfirmationProps> = ({
  rows,
  mobile,
  activeBooking,
  onRefreshPayment,
  onResendWhatsApp,
  isRefreshingPayment,
}) => {
  const { addAMPMToTime, veticDateFormat } = useUtils();
  const isPaidBooking = !!activeBooking?.payment_collected;
  const isPrepaidPaymentPending = !!activeBooking?.payment_pending;
  const showOnHoldBanner = isPaidBooking && isPrepaidPaymentPending;
  return (
    <div className="space-y-4 font-sans">
      <h3 className="text-[14px] font-bold text-black font-sans">Appointment Details</h3>

      {showOnHoldBanner ? (
        <div className="w-full h-[62.5px] rounded-[10px] gap-[8px] border bg-[#FFFBED] border-[#FEE1C0] p-[8px] flex items-center">
          <img src={onHold} className="w-5 h-5 shrink-0" alt="on hold" />
          <div className="flex flex-col justify-center">
            <p className="text-[14px] font-semibold text-[#533200] leading-tight">
              Booking on hold - Payment not received yet.
            </p>
            <p className="text-xs font-medium text-black leading-tight mt-0.5">
              This slot is reserved for 15 min. Complete payment to confirm booking or the slot will be released.
            </p>
          </div>
        </div>
      ) : (
        <div className="w-full h-[62.5px] rounded-[10px] gap-[8px] border bg-[#EDFFEE] border-[#C0FECA] p-[8px] flex items-center">
          <CheckIcon className="w-5 h-5 text-[#005303] shrink-0" />
          <div className="flex flex-col justify-center">
            <p className="text-[14px] font-semibold text-[#005303] leading-tight">
              {isPaidBooking ? "Booking Created Successfully" : "Booking Confirmed"}
            </p>
            <p className="text-xs font-medium text-black leading-tight mt-0.5">
              {isPaidBooking
                ? `Appointment has been confirmed. Payment Collected.${mobile ? ` Confirmation sent to phone number ${mobile}.` : ""}`
                : `Your appointment has been created successfully.${mobile ? ` Confirmation sent to ${mobile}.` : ""}`}
            </p>
          </div>
        </div>
      )}

      <div className="w-full border border-[#D7D7D7] rounded-[10px] overflow-hidden bg-[#FBFCFD]">
        <table className="w-full text-left table-fixed">
          <thead>
            <tr className="bg-[#F6F7F8] h-[50px] border-b border-[#D7D7D7]">
              {[
                { name: "Parent", width: "w-[13%]" },
                { name: "Pet Details", width: "w-[15%]" },
                { name: "Service", width: "w-[18%]" },
                { name: "Time", width: "w-[13%]" },
                { name: "Provider", width: "w-[18%]" },
                { name: "Clinic", width: "w-[23%]" },
              ].map((h, index, arr) => (
                <th
                  key={h.name}
                  scope="col"
                  className={`py-[16px] text-[14px] font-semibold text-[#193A6A] ${h.width} ${index === 0 ? "pl-[20px] pr-[16px]" : index === arr.length - 1 ? "pl-[16px] pr-[20px]" : "px-[16px]"
                    }`}
                >
                  {h.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeBooking && (
              <tr
                key={activeBooking.booking_id || "booking"}
                className="border-b border-[#D7D7D7] last:border-b-0 h-[64px] bg-[#FBFCFD] align-top"
              >
                <td className="pl-[20px] pr-[16px] py-[16px] text-xs font-semibold text-black">
                  {activeBooking.parent_name || "-"}
                </td>
                <td className="px-[16px] py-[16px] text-xs">
                  <div className="text-xs font-semibold text-black">{activeBooking.pet_name || "-"}</div>
                  <div className="text-xs font-semibold text-[#727272] mt-0.5">
                    {joinBreed(activeBooking.pet_species_name, activeBooking.pet_breed_name)}
                  </div>
                </td>
                <td className="px-[16px] py-[16px] text-xs">
                  <div className="text-xs font-semibold text-black">
                    {activeBooking.service_type_name || "-"}
                  </div>
                  <div className="text-xs font-semibold text-[#727272] mt-0.5">
                    {activeBooking.sub_service_name || "-"}
                  </div>
                </td>
                <td className="px-[16px] py-[16px] text-xs">
                  <div className="text-xs font-semibold text-black">
                    {addAMPMToTime(activeBooking.start_time)}
                  </div>
                  <div className="text-xs font-semibold text-[#727272] mt-0.5">
                    {veticDateFormat(activeBooking.date)}
                  </div>
                </td>
                <td className="px-[16px] py-[16px] text-xs font-semibold text-black">
                  {activeBooking.staff_name || "-"}
                </td>
                <td className="pr-[20px] pl-[16px] py-[16px] text-xs font-semibold text-[#727272]">
                  {activeBooking.clinic_name || "-"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {activeBooking?.payment_pending && (
        <div
          className="w-full h-[160px] rounded-[10px] border border-[#808DA033] bg-white p-[16px] flex flex-col justify-between font-sans shadow-[0_2px_10px_rgba(17,24,39,0.05)]"
        >
          <div>
            <h3 className="text-[14px] font-semibold text-black font-sans">
              Advance Payment Details
            </h3>

            <div className="mt-4 grid grid-cols-3 gap-8">
              <div>
                <p className="text-xs font-medium text-[#6D6D6D] font-sans">Advance Payment</p>
                <p className="text-[14px] font-semibold text-[#222222] font-sans mt-1">
                  ₹{activeBooking.booking_fee ?? 0}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium text-[#6D6D6D] font-sans">Payment Status</p>
                <p className="text-[14px] font-semibold text-[#FFB814] font-sans mt-1">
                  • Pending
                </p>
              </div>

              <div>
                <p className="text-xs font-medium text-[#6D6D6D] font-sans">Payment Number</p>
                <p className="text-[14px] font-semibold text-[#222222] font-sans mt-1">
                  {mobile}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center mt-4">
            <button
              type="button"
              onClick={() => onRefreshPayment?.(activeBooking.booking_id)}
              disabled={isRefreshingPayment}
              className="inline-flex items-center justify-center gap-2 rounded-[8px] border border-black h-[40px] px-[16px] text-[14px] font-semibold text-black hover:bg-slate-50 transition-all focus:outline-none"
            >
              {isRefreshingPayment ? (
                <svg className="animate-spin h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <>
                  <RefreshCcw className="h-4 w-4" />
                  Refresh
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => onResendWhatsApp?.(activeBooking.booking_id, activeBooking.pet_name)}
              className="h-[40px] px-[16px] bg-[#00873C] hover:bg-[#007031] active:bg-[#005c28] text-white rounded-[8px] text-[14px] font-semibold transition-all focus:outline-none flex items-center justify-center font-sans"
            >
              Resend WhatsApp / Send to Another Number
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
